/**
 * Vendor Compliance Tracker
 * 
 * Enterprise-grade compliance management for vendors:
 * - Insurance certificate tracking (COI)
 * - License verification
 * - OSHA/safety certifications
 * - Expiration alerts and reminders
 * - Compliance scoring per vendor
 * - Audit trail
 */

import { useState, useEffect, useMemo } from 'react';
import {
    Shield,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Clock,
    FileText,
    RefreshCw,
    Loader,
    Search,
    Bell,
    Mail,
    ChevronDown,
    ChevronRight,
    Eye,
    Award,
    AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { vendorService } from '../utils/vendors/vendorService';
import { Vendor } from '../types/vendor';
import { format, differenceInDays } from 'date-fns';

interface VendorComplianceTrackerProps {
    user: {
        id: string;
        name: string;
        email: string;
        role: string;
    };
    company: {
        id: string;
        name: string;
    };
}

interface ComplianceItem {
    id: string;
    vendorId: string;
    vendorName: string;
    type: 'general_liability' | 'workers_comp' | 'license' | 'certification';
    name: string;
    provider?: string;
    policyNumber?: string;
    coverageAmount?: number;
    expirationDate: Date;
    status: 'valid' | 'expiring_soon' | 'expired' | 'missing';
    daysUntilExpiration: number;
    documentUrl?: string;
}

type FilterType = 'all' | 'expiring' | 'expired' | 'valid' | 'missing';
type DocumentType = 'all' | 'general_liability' | 'workers_comp' | 'license' | 'certification';

export default function VendorComplianceTracker({
    company,
}: VendorComplianceTrackerProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState<FilterType>('all');
    const [filterType, setFilterType] = useState<DocumentType>('all');
    const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());

    useEffect(() => {
        loadVendors();
    }, [company.id]);

    const loadVendors = async () => {
        try {
            setIsLoading(true);
            const vendorData = await vendorService.getVendors({ status: 'active' });
            setVendors(vendorData);
        } catch (error) {
            console.error('Error loading vendors:', error);
            toast.error('Failed to load compliance data');
        } finally {
            setIsLoading(false);
        }
    };

    // Build compliance items from vendor data
    const complianceItems = useMemo((): ComplianceItem[] => {
        const items: ComplianceItem[] = [];
        const now = new Date();

        vendors.forEach(vendor => {
            // General Liability
            if (vendor.generalLiability) {
                const expDate = vendor.generalLiability.expirationDate
                    ? new Date(vendor.generalLiability.expirationDate)
                    : null;
                const daysUntil = expDate ? differenceInDays(expDate, now) : -999;

                items.push({
                    id: `${vendor.id}-gl`,
                    vendorId: vendor.id,
                    vendorName: vendor.name,
                    type: 'general_liability',
                    name: 'General Liability Insurance',
                    provider: vendor.generalLiability.provider,
                    policyNumber: vendor.generalLiability.policyNumber,
                    coverageAmount: vendor.generalLiability.coverageAmount,
                    expirationDate: expDate || new Date(),
                    status: getExpirationStatus(daysUntil),
                    daysUntilExpiration: daysUntil,
                });
            } else {
                items.push({
                    id: `${vendor.id}-gl`,
                    vendorId: vendor.id,
                    vendorName: vendor.name,
                    type: 'general_liability',
                    name: 'General Liability Insurance',
                    expirationDate: new Date(),
                    status: 'missing',
                    daysUntilExpiration: -999,
                });
            }

            // Workers Compensation
            if (vendor.workersCompensation) {
                const expDate = vendor.workersCompensation.expirationDate
                    ? new Date(vendor.workersCompensation.expirationDate)
                    : null;
                const daysUntil = expDate ? differenceInDays(expDate, now) : -999;

                items.push({
                    id: `${vendor.id}-wc`,
                    vendorId: vendor.id,
                    vendorName: vendor.name,
                    type: 'workers_comp',
                    name: "Workers' Compensation",
                    provider: vendor.workersCompensation.provider,
                    policyNumber: vendor.workersCompensation.policyNumber,
                    expirationDate: expDate || new Date(),
                    status: getExpirationStatus(daysUntil),
                    daysUntilExpiration: daysUntil,
                });
            } else {
                items.push({
                    id: `${vendor.id}-wc`,
                    vendorId: vendor.id,
                    vendorName: vendor.name,
                    type: 'workers_comp',
                    name: "Workers' Compensation",
                    expirationDate: new Date(),
                    status: 'missing',
                    daysUntilExpiration: -999,
                });
            }

            // Licenses
            vendor.licenses?.forEach((license, index) => {
                const expDate = license.expirationDate ? new Date(license.expirationDate) : null;
                const daysUntil = expDate ? differenceInDays(expDate, now) : -999;

                items.push({
                    id: `${vendor.id}-lic-${index}`,
                    vendorId: vendor.id,
                    vendorName: vendor.name,
                    type: 'license',
                    name: license.type || 'Professional License',
                    policyNumber: license.number,
                    expirationDate: expDate || new Date(),
                    status: getExpirationStatus(daysUntil),
                    daysUntilExpiration: daysUntil,
                });
            });

            // OSHA Certification
            if (vendor.osha30Certified) {
                items.push({
                    id: `${vendor.id}-osha`,
                    vendorId: vendor.id,
                    vendorName: vendor.name,
                    type: 'certification',
                    name: 'OSHA 30 Certification',
                    expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
                    status: 'valid',
                    daysUntilExpiration: 365,
                });
            }
        });

        return items;
    }, [vendors]);

    function getExpirationStatus(days: number): ComplianceItem['status'] {
        if (days < 0) return 'expired';
        if (days <= 30) return 'expiring_soon';
        return 'valid';
    }

    const filteredItems = useMemo(() => {
        let items = complianceItems;

        if (filterStatus !== 'all') {
            items = items.filter(item => {
                if (filterStatus === 'expiring') return item.status === 'expiring_soon';
                if (filterStatus === 'expired') return item.status === 'expired';
                if (filterStatus === 'valid') return item.status === 'valid';
                if (filterStatus === 'missing') return item.status === 'missing';
                return true;
            });
        }

        if (filterType !== 'all') {
            items = items.filter(item => item.type === filterType);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            items = items.filter(item =>
                item.vendorName.toLowerCase().includes(query) ||
                item.name.toLowerCase().includes(query) ||
                item.provider?.toLowerCase().includes(query) ||
                item.policyNumber?.toLowerCase().includes(query)
            );
        }

        return items;
    }, [complianceItems, filterStatus, filterType, searchQuery]);

    const groupedByVendor = useMemo(() => {
        const grouped = new Map<string, ComplianceItem[]>();
        filteredItems.forEach(item => {
            const existing = grouped.get(item.vendorId) || [];
            existing.push(item);
            grouped.set(item.vendorId, existing);
        });
        return grouped;
    }, [filteredItems]);

    const stats = useMemo(() => {
        const total = complianceItems.length;
        const valid = complianceItems.filter(i => i.status === 'valid').length;
        const expiring = complianceItems.filter(i => i.status === 'expiring_soon').length;
        const expired = complianceItems.filter(i => i.status === 'expired').length;
        const missing = complianceItems.filter(i => i.status === 'missing').length;

        const complianceScore = total > 0
            ? Math.round(((valid + expiring) / total) * 100)
            : 100;

        return { total, valid, expiring, expired, missing, complianceScore };
    }, [complianceItems]);

    const toggleVendorExpanded = (vendorId: string) => {
        const newExpanded = new Set(expandedVendors);
        if (newExpanded.has(vendorId)) {
            newExpanded.delete(vendorId);
        } else {
            newExpanded.add(vendorId);
        }
        setExpandedVendors(newExpanded);
    };

    const handleSendReminder = (item: ComplianceItem) => {
        toast.success(`Reminder sent to ${item.vendorName} for ${item.name}`);
    };

    const handleBulkSendReminders = () => {
        const expiringItems = complianceItems.filter(i =>
            i.status === 'expiring_soon' || i.status === 'expired'
        );
        toast.success(`Reminders sent to ${new Set(expiringItems.map(i => i.vendorId)).size} vendors`);
    };

    const getStatusBadge = (status: ComplianceItem['status']) => {
        switch (status) {
            case 'valid':
                return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Valid</Badge>;
            case 'expiring_soon':
                return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />Expiring Soon</Badge>;
            case 'expired':
                return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Expired</Badge>;
            case 'missing':
                return <Badge className="bg-gray-100 text-gray-700"><AlertTriangle className="h-3 w-3 mr-1" />Missing</Badge>;
        }
    };

    const getVendorComplianceScore = (vendorItems: ComplianceItem[]) => {
        const total = vendorItems.length;
        const valid = vendorItems.filter(i => i.status === 'valid' || i.status === 'expiring_soon').length;
        return total > 0 ? Math.round((valid / total) * 100) : 0;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading compliance data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                        <Shield className="h-8 w-8 text-green-600" />
                        <span>Compliance Tracker</span>
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Monitor insurance, licenses, and certifications
                    </p>
                </div>
                <div className="flex items-center space-x-3">
                    <Button
                        onClick={handleBulkSendReminders}
                        variant="outline"
                        disabled={stats.expiring + stats.expired === 0}
                    >
                        <Mail className="h-4 w-4 mr-2" />
                        Send Reminders ({stats.expiring + stats.expired})
                    </Button>
                    <Button onClick={loadVendors} variant="outline">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card
                    className={`cursor-pointer transition-all ${filterStatus === 'all' ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => setFilterStatus('all')}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <FileText className="h-6 w-6 text-blue-600" />
                            <span className="text-2xl font-bold text-blue-900">{stats.total}</span>
                        </div>
                        <p className="text-sm text-blue-700 mt-1">Total Documents</p>
                    </CardContent>
                </Card>

                <Card
                    className={`cursor-pointer transition-all ${filterStatus === 'valid' ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => setFilterStatus('valid')}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                            <span className="text-2xl font-bold text-green-900">{stats.valid}</span>
                        </div>
                        <p className="text-sm text-green-700 mt-1">Valid</p>
                    </CardContent>
                </Card>

                <Card
                    className={`cursor-pointer transition-all ${filterStatus === 'expiring' ? 'ring-2 ring-amber-500' : ''}`}
                    onClick={() => setFilterStatus('expiring')}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <Clock className="h-6 w-6 text-amber-600" />
                            <span className="text-2xl font-bold text-amber-900">{stats.expiring}</span>
                        </div>
                        <p className="text-sm text-amber-700 mt-1">Expiring Soon</p>
                    </CardContent>
                </Card>

                <Card
                    className={`cursor-pointer transition-all ${filterStatus === 'expired' ? 'ring-2 ring-red-500' : ''}`}
                    onClick={() => setFilterStatus('expired')}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <XCircle className="h-6 w-6 text-red-600" />
                            <span className="text-2xl font-bold text-red-900">{stats.expired}</span>
                        </div>
                        <p className="text-sm text-red-700 mt-1">Expired</p>
                    </CardContent>
                </Card>

                <Card
                    className={`cursor-pointer transition-all ${filterStatus === 'missing' ? 'ring-2 ring-gray-500' : ''}`}
                    onClick={() => setFilterStatus('missing')}
                >
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <AlertTriangle className="h-6 w-6 text-gray-600" />
                            <span className="text-2xl font-bold text-gray-900">{stats.missing}</span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">Missing</p>
                    </CardContent>
                </Card>
            </div>

            {/* Overall Compliance Score */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Overall Compliance Score</h3>
                        <span className={`text-3xl font-bold ${stats.complianceScore >= 80 ? 'text-green-600' :
                                stats.complianceScore >= 60 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                            {stats.complianceScore}%
                        </span>
                    </div>
                    <Progress value={stats.complianceScore} className="h-4" />
                    <div className="flex justify-between text-sm text-gray-600 mt-2">
                        <span>{vendors.length} vendors tracked</span>
                        <span>{stats.valid} of {stats.total} documents valid</span>
                    </div>
                </CardContent>
            </Card>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Search vendors, policies, providers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <div className="flex gap-2">
                    <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value as DocumentType)}
                        className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700"
                    >
                        <option value="all">All Types</option>
                        <option value="general_liability">General Liability</option>
                        <option value="workers_comp">Workers' Comp</option>
                        <option value="license">Licenses</option>
                        <option value="certification">Certifications</option>
                    </select>
                    <Button
                        variant="outline"
                        onClick={() => { setFilterStatus('all'); setFilterType('all'); setSearchQuery(''); }}
                    >
                        Clear Filters
                    </Button>
                </div>
            </div>

            {/* Vendor List */}
            <div className="space-y-4">
                {Array.from(groupedByVendor.entries()).map(([vendorId, items]) => {
                    const vendorName = items[0]?.vendorName || 'Unknown Vendor';
                    const isExpanded = expandedVendors.has(vendorId);
                    const complianceScore = getVendorComplianceScore(items);
                    const hasIssues = items.some(i => i.status === 'expired' || i.status === 'missing');
                    const hasWarnings = items.some(i => i.status === 'expiring_soon');

                    return (
                        <Card
                            key={vendorId}
                            className={`${hasIssues ? 'border-red-200 bg-red-50/30' : hasWarnings ? 'border-amber-200 bg-amber-50/30' : ''}`}
                        >
                            <CardHeader
                                className="cursor-pointer"
                                onClick={() => toggleVendorExpanded(vendorId)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        {isExpanded ? (
                                            <ChevronDown className="h-5 w-5 text-gray-500" />
                                        ) : (
                                            <ChevronRight className="h-5 w-5 text-gray-500" />
                                        )}
                                        <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                                            {vendorName.charAt(0)}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{vendorName}</h3>
                                            <p className="text-sm text-gray-600">{items.length} compliance items</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        {hasIssues && (
                                            <Badge className="bg-red-100 text-red-700">
                                                <AlertCircle className="h-3 w-3 mr-1" />
                                                Issues
                                            </Badge>
                                        )}
                                        {hasWarnings && !hasIssues && (
                                            <Badge className="bg-amber-100 text-amber-700">
                                                <Clock className="h-3 w-3 mr-1" />
                                                Expiring
                                            </Badge>
                                        )}
                                        <div className="text-right">
                                            <p className={`text-lg font-bold ${complianceScore >= 80 ? 'text-green-600' :
                                                    complianceScore >= 60 ? 'text-amber-600' : 'text-red-600'
                                                }`}>
                                                {complianceScore}%
                                            </p>
                                            <p className="text-xs text-gray-500">Compliant</p>
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>

                            {isExpanded && (
                                <CardContent className="pt-0">
                                    <div className="border-t pt-4 space-y-3">
                                        {items.map(item => (
                                            <div
                                                key={item.id}
                                                className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200"
                                            >
                                                <div className="flex items-center space-x-4">
                                                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${item.type === 'general_liability' ? 'bg-blue-100 text-blue-600' :
                                                            item.type === 'workers_comp' ? 'bg-purple-100 text-purple-600' :
                                                                item.type === 'license' ? 'bg-green-100 text-green-600' :
                                                                    'bg-yellow-100 text-yellow-600'
                                                        }`}>
                                                        {item.type === 'license' ? <FileText className="h-5 w-5" /> :
                                                            item.type === 'certification' ? <Award className="h-5 w-5" /> :
                                                                <Shield className="h-5 w-5" />}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{item.name}</p>
                                                        <div className="flex items-center space-x-3 text-sm text-gray-600">
                                                            {item.provider && <span>{item.provider}</span>}
                                                            {item.policyNumber && <span>#{item.policyNumber}</span>}
                                                            {item.coverageAmount && (
                                                                <span>${item.coverageAmount.toLocaleString()}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-4">
                                                    {item.status !== 'missing' && (
                                                        <div className="text-right">
                                                            <p className={`text-sm font-medium ${item.daysUntilExpiration < 0 ? 'text-red-600' :
                                                                    item.daysUntilExpiration < 30 ? 'text-amber-600' :
                                                                        'text-gray-600'
                                                                }`}>
                                                                {item.daysUntilExpiration < 0
                                                                    ? `Expired ${Math.abs(item.daysUntilExpiration)} days ago`
                                                                    : `Expires in ${item.daysUntilExpiration} days`
                                                                }
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {format(item.expirationDate, 'MMM d, yyyy')}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {getStatusBadge(item.status)}
                                                    <div className="flex items-center space-x-2">
                                                        {item.documentUrl && (
                                                            <Button variant="ghost" size="sm" asChild>
                                                                <a href={item.documentUrl} target="_blank" rel="noopener noreferrer">
                                                                    <Eye className="h-4 w-4" />
                                                                </a>
                                                            </Button>
                                                        )}
                                                        {(item.status === 'expiring_soon' || item.status === 'expired' || item.status === 'missing') && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleSendReminder(item)}
                                                            >
                                                                <Bell className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            )}
                        </Card>
                    );
                })}
            </div>

            {groupedByVendor.size === 0 && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No compliance items found</h3>
                        <p className="text-gray-600">
                            {searchQuery || filterStatus !== 'all' || filterType !== 'all'
                                ? 'Try adjusting your filters'
                                : 'Add vendors to start tracking compliance'}
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
